/** This file must only contain pure code and pure imports */

import { AbstractMesh } from "core/Meshes/abstractMesh.pure";
import { type Nullable } from "../../types";
import { AbstractEngine } from "../abstractEngine.pure";

/** @internal */
export type OcclusionQuery = WebGLQuery | number;

/** @internal */
// eslint-disable-next-line @typescript-eslint/naming-convention
export class _OcclusionDataStorage {
    /** @internal */
    public occlusionInternalRetryCounter = 0;

    /** @internal */
    public isOcclusionQueryInProgress = false;

    /** @internal */
    public isOccluded = false;

    /** @internal */
    public occlusionRetryCount = -1;

    /** @internal */
    public occlusionType = AbstractMesh.OCCLUSION_TYPE_NONE;

    /** @internal */
    public occlusionQueryAlgorithmType = AbstractMesh.OCCLUSION_ALGORITHM_TYPE_CONSERVATIVE;

    /** @internal */
    public forceRenderingWhenOccluded = false;

    /** @internal */
    public occlusionForRenderPassId = -1;
}

let _Registered = false;
/**
 * Register side effects for abstractEngineQuery.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterAbstractEngineQuery(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    AbstractEngine.prototype.createQuery = function (): Nullable<OcclusionQuery> {
        return null;
    };

    AbstractEngine.prototype.deleteQuery = function (query: OcclusionQuery): AbstractEngine {
        // Do nothing. Must be implemented by child classes
        return this;
    };

    AbstractEngine.prototype.isQueryResultAvailable = function (query: OcclusionQuery): boolean {
        // Do nothing. Must be implemented by child classes
        return false;
    };

    AbstractEngine.prototype.getQueryResult = function (query: OcclusionQuery): number {
        // Do nothing. Must be implemented by child classes
        return 0;
    };

    AbstractEngine.prototype.isOcclusionQueryVisible = function (query: OcclusionQuery): boolean {
        return this.getQueryResult(query) > 0;
    };

    AbstractEngine.prototype.beginOcclusionQuery = function (algorithmType: number, query: OcclusionQuery): boolean {
        // Do nothing. Must be implemented by child classes
        return false;
    };

    AbstractEngine.prototype.endOcclusionQuery = function (algorithmType: number): AbstractEngine {
        // Do nothing. Must be implemented by child classes
        return this;
    };

    Object.defineProperty(AbstractMesh.prototype, "isOcclusionQueryInProgress", {
        get: function (this: AbstractMesh) {
            return this._occlusionDataStorage.isOcclusionQueryInProgress;
        },
        set: function (this: AbstractMesh, value: boolean) {
            this._occlusionDataStorage.isOcclusionQueryInProgress = value;
        },
        enumerable: false,
        configurable: true,
    });

    Object.defineProperty(AbstractMesh.prototype, "_occlusionDataStorage", {
        get: function (this: AbstractMesh) {
            if (!this.__occlusionDataStorage) {
                this.__occlusionDataStorage = new _OcclusionDataStorage();
            }
            return this.__occlusionDataStorage;
        },
        enumerable: false,
        configurable: true,
    });

    Object.defineProperty(AbstractMesh.prototype, "isOccluded", {
        get: function (this: AbstractMesh) {
            return this._occlusionDataStorage.isOccluded;
        },
        set: function (this: AbstractMesh, value: boolean) {
            this._occlusionDataStorage.isOccluded = value;
        },
        enumerable: true,
        configurable: true,
    });

    Object.defineProperty(AbstractMesh.prototype, "occlusionQueryAlgorithmType", {
        get: function (this: AbstractMesh) {
            return this._occlusionDataStorage.occlusionQueryAlgorithmType;
        },
        set: function (this: AbstractMesh, value: number) {
            this._occlusionDataStorage.occlusionQueryAlgorithmType = value;
        },
        enumerable: true,
        configurable: true,
    });

    Object.defineProperty(AbstractMesh.prototype, "occlusionType", {
        get: function (this: AbstractMesh) {
            return this._occlusionDataStorage.occlusionType;
        },
        set: function (this: AbstractMesh, value: number) {
            this._occlusionDataStorage.occlusionType = value;
        },
        enumerable: true,
        configurable: true,
    });

    Object.defineProperty(AbstractMesh.prototype, "occlusionRetryCount", {
        get: function (this: AbstractMesh) {
            return this._occlusionDataStorage.occlusionRetryCount;
        },
        set: function (this: AbstractMesh, value: number) {
            this._occlusionDataStorage.occlusionRetryCount = value;
        },
        enumerable: true,
        configurable: true,
    });

    Object.defineProperty(AbstractMesh.prototype, "forceRenderingWhenOccluded", {
        get: function (this: AbstractMesh) {
            return this._occlusionDataStorage.forceRenderingWhenOccluded;
        },
        set: function (this: AbstractMesh, value: boolean) {
            this._occlusionDataStorage.forceRenderingWhenOccluded = value;
        },
        enumerable: true,
        configurable: true,
    });

    Object.defineProperty(AbstractMesh.prototype, "occlusionForRenderPassId", {
        get: function (this: AbstractMesh) {
            return this._occlusionDataStorage.occlusionForRenderPassId;
        },
        set: function (this: AbstractMesh, value: number) {
            this._occlusionDataStorage.occlusionForRenderPassId = value;
        },
        enumerable: true,
        configurable: true,
    });

    // We also need to update AbstractMesh as there is a portion of the code there
    AbstractMesh.prototype._checkOcclusionQuery = function (checkOnly: boolean) {
        const dataStorage = this._occlusionDataStorage;

        if (dataStorage.occlusionType === AbstractMesh.OCCLUSION_TYPE_NONE) {
            dataStorage.isOccluded = false;
            return false;
        }

        const engine = this.getEngine();

        if (!engine.getCaps().supportOcclusionQuery) {
            dataStorage.isOccluded = false;
            return false;
        }

        if (!engine.isQueryResultAvailable) {
            // Occlusion query where not referenced
            dataStorage.isOccluded = false;
            return false;
        }

        if (this.isOcclusionQueryInProgress && this._occlusionQuery !== null && this._occlusionQuery !== undefined) {
            const isOcclusionQueryAvailable = engine.isQueryResultAvailable(this._occlusionQuery);
            if (isOcclusionQueryAvailable) {
                dataStorage.isOcclusionQueryInProgress = false;
                dataStorage.occlusionInternalRetryCounter = 0;
                dataStorage.isOccluded = !engine.isOcclusionQueryVisible(this._occlusionQuery);
            } else {
                dataStorage.occlusionInternalRetryCounter++;

                if (dataStorage.occlusionRetryCount !== -1 && dataStorage.occlusionInternalRetryCounter > dataStorage.occlusionRetryCount) {
                    dataStorage.isOcclusionQueryInProgress = false;
                    dataStorage.occlusionInternalRetryCounter = 0;

                    // if optimistic set isOccluded to false regardless of the status of isOccluded. (Render in the current render loop)
                    // if strict continue the last state of the object.
                    dataStorage.isOccluded = dataStorage.occlusionType === AbstractMesh.OCCLUSION_TYPE_OPTIMISTIC ? false : dataStorage.isOccluded;
                } else {
                    return dataStorage.occlusionType === AbstractMesh.OCCLUSION_TYPE_OPTIMISTIC ? false : dataStorage.isOccluded;
                }
            }
        }

        if (checkOnly) {
            return dataStorage.isOccluded;
        }

        const scene = this.getScene();
        if (scene.getBoundingBoxRenderer) {
            const occlusionBoundingBoxRenderer = scene.getBoundingBoxRenderer();

            if (this._occlusionQuery === null) {
                this._occlusionQuery = engine.createQuery();
            }

            if (this._occlusionQuery && engine.beginOcclusionQuery(dataStorage.occlusionQueryAlgorithmType, this._occlusionQuery)) {
                occlusionBoundingBoxRenderer.renderOcclusionBoundingBox(this);
                engine.endOcclusionQuery(dataStorage.occlusionQueryAlgorithmType);
                this._occlusionDataStorage.isOcclusionQueryInProgress = true;
            }
        }

        return dataStorage.isOccluded;
    };
}
